import { ComponentType } from "react";
import { ControllerRenderProps, FieldValues } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { InputPassword } from "@/components/ui/input-password";
import InputCpf from "@/components/ui/input-cpf";
import InputCpfCnpj from "@/components/ui/input-cpf-cnpj";
import InputPhone from "@/components/ui/input-phone";
import InputCep from "@/components/ui/input-cep";
import InputRg from "@/components/ui/input-rg";
import CurrencyInput from "@/components/ui/currency-input";
import DatePicker from "@/components/ui/date-picker";
import { Textarea } from "@/components/ui/textarea";
import { InputFile } from "@/components/ui/input-file";
import { FieldConfig, FieldType } from "./types";
import InputDateInline from "../ui/input-date-inline";

/**
 * Tipo do componente de campo
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type FieldComponent = ComponentType<any>;

/**
 * Entrada do registro de campos
 */
export interface FieldRegistryEntry {
  component: FieldComponent | null;
  propsMapper: (
    field: ControllerRenderProps<FieldValues, string>,
    config: FieldConfig
  ) => Record<string, unknown>;
}

/**
 * Registro de componentes por tipo de campo
 * Mapeia cada FieldType para seu componente e função de mapeamento de props
 */
export const fieldRegistry: Record<FieldType, FieldRegistryEntry> = {
  text: {
    component: Input as FieldComponent,
    propsMapper: (field, config) => ({
      ...field,
      type: "text",
      placeholder: config.placeholder,
      disabled: config.disabled,
      readOnly: config.readOnly,
    }),
  },

  email: {
    component: Input as FieldComponent,
    propsMapper: (field, config) => ({
      ...field,
      type: "email",
      placeholder: config.placeholder || "email@exemplo.com",
      disabled: config.disabled,
      readOnly: config.readOnly,
    }),
  },

  password: {
    component: InputPassword as FieldComponent,
    propsMapper: (field, config) => ({
      field,
      placeholder: config.placeholder || "••••••••",
      disabled: config.disabled,
    }),
  },

  textarea: {
    component: Textarea as FieldComponent,
    propsMapper: (field, config) => ({
      ...field,
      placeholder: config.placeholder,
      disabled: config.disabled,
      readOnly: config.readOnly,
      rows: 4,
    }),
  },

  number: {
    component: Input as FieldComponent,
    propsMapper: (field, config) => ({
      ...field,
      type: "number",
      placeholder: config.placeholder,
      min: config.min,
      max: config.max,
      disabled: config.disabled,
      readOnly: config.readOnly,
    }),
  },

  cpf: {
    component: InputCpf as FieldComponent,
    propsMapper: (field, config) => ({
      field,
      placeholder: config.placeholder || "000.000.000-00",
      disabled: config.disabled,
    }),
  },

  cnpj: {
    component: InputCpfCnpj as FieldComponent, // Usando o mesmo componente que suporta ambos
    propsMapper: (field, config) => ({
      field,
      placeholder: config.placeholder || "00.000.000/0000-00",
      disabled: config.disabled,
    }),
  },

  "cpf-cnpj": {
    component: InputCpfCnpj as FieldComponent,
    propsMapper: (field, config) => ({
      field,
      placeholder: config.placeholder || "CPF ou CNPJ",
      disabled: config.disabled,
    }),
  },

  phone: {
    component: InputPhone as FieldComponent,
    propsMapper: (field, config) => ({
      field,
      placeholder: config.placeholder || "(00) 00000-0000",
      disabled: config.disabled,
    }),
  },

  cep: {
    component: InputCep as FieldComponent,
    propsMapper: (field, config) => ({
      field,
      placeholder: config.placeholder || "00000-000",
      disabled: config.disabled,
    }),
  },

  rg: {
    component: InputRg as FieldComponent,
    propsMapper: (field, config) => ({
      field,
      placeholder: config.placeholder || "00.000.000-0",
      disabled: config.disabled,
    }),
  },

  currency: {
    component: CurrencyInput as FieldComponent,
    propsMapper: (field, config) => ({
      field,
      placeholder: config.placeholder || "R$ 0,00",
      disabled: config.disabled,
    }),
  },

  date: {
    component: DatePicker as FieldComponent,
    propsMapper: (field, config) => ({
      field: field,
      placeholder: config.placeholder || "Selecione uma data",
      disabled: config.disabled,
    }),
  },

  // Select, radio, multi-select são tratados especialmente no DynamicFormField
  select: {
    component: null,
    propsMapper: (field, config) => ({
      value: field.value,
      onValueChange: field.onChange,
      disabled: config.disabled,
      options: config.options,
      placeholder: config.placeholder || "Selecione...",
    }),
  },

  "multi-select": {
    component: null,
    propsMapper: (field, config) => ({
      value: field.value,
      onChange: field.onChange,
      options: config.options,
      disabled: config.disabled,
    }),
  },

  radio: {
    component: null,
    propsMapper: (field, config) => ({
      value: field.value,
      onValueChange: field.onChange,
      options: config.options,
      disabled: config.disabled,
    }),
  },

  checkbox: {
    component: null, // Tratado especialmente
    propsMapper: (field, config) => ({
      checked: field.value,
      onCheckedChange: field.onChange,
      disabled: config.disabled,
      label: config.label,
    }),
  },

  file: {
    component: InputFile as FieldComponent,
    propsMapper: (field, config) => ({
      field,
      placeholder: config.placeholder,
      onFileSelect: config.onFileSelect,
      disabled: config.disabled,
    }),
  },

  "file-preview": {
    component: null,
    propsMapper: () => ({}),
  },

  "date-inline": {
    component: InputDateInline as FieldComponent,
    propsMapper: (field, config) => ({
      field,
      placeholder: config.placeholder || "00/00/0000",
      disabled: config.disabled,
    }),
  },

  custom: {
    component: null,
    propsMapper: () => ({}),
  },
};

/**
 * Obtém a entrada do registro para um tipo de campo
 */
export function getFieldRegistryEntry(
  type: FieldType
): FieldRegistryEntry | null {
  return fieldRegistry[type] || null;
}

/**
 * Verifica se um tipo de campo tem componente no registro
 */
export function hasFieldComponent(type: FieldType): boolean {
  return fieldRegistry[type]?.component !== null;
}

/**
 * Tipos que requerem tratamento especial (não usam o registry diretamente)
 */
export const specialFieldTypes: FieldType[] = [
  "select",
  "multi-select",
  "radio",
  "checkbox",
  "custom",
];

/**
 * Verifica se um tipo de campo requer tratamento especial
 */
export function isSpecialFieldType(type: FieldType): boolean {
  return specialFieldTypes.includes(type);
}
